import _ from 'lodash';
import * as paper from 'paper';
import { getItemChildrens, getItemParents, Unit, units } from 'src/components/widgets/areaDesigner/helpers';
import { ResizeIcon } from 'src/components/widgets/areaDesigner/icons';
import theme from 'src/shared/theme';
import { DeleteIcon, MoveIcon, RotateIcon } from './icons';
import { Area, isArea, isPlacementItem, Layer, PlacementItem, PlacementLayer } from './placementAreaDesigner';
import { PlacementAreaDesignerAreaSizePlugin } from './plugins/placementAreaDesignerAreaSizePlugin';
import { placementAreaDesignerDocumentSizePlugin } from './plugins/placementAreaDesignerDocumentSizePlugin';
import PlacementAreaDesignerPlugin from './plugins/placementAreaDesignerPlugin';
import { PlacementTool, ZIndexType } from './shared/sharedPlacementComponents';

export type Mode = 'PLACEMENT';

export interface PlacementAreaDesignerEvents {
    onItemsChanged?: (item: (Area | PlacementItem)[], newPPCM: number | null, layers: Layer[]) => void;
    onAreaRemoved?: (area: Area | PlacementItem) => void;
    onPPCMChanged?: (ppcm: number) => void;
    onItemCreated?: (item: paper.Item) => void;
    // onItemSelected?: (item?: Area | PlacementItem | null, isMultipleSelection?: boolean) => void;
    onItemsSelected?: (item?: (Area | PlacementItem)[] | null, referenceItemGuid?: string | null) => void;
}

export class PlacementAreaDesignerManager {
    private HANDLE_COLOR = new paper.Color(theme.app.backgroundColor);
    private HANDLE_ICON_COLOR = new paper.Color(theme.app.primary);
    private HANDLE_HOVER_COLOR = new paper.Color(theme.app.primary_dark);

    private AREA_STROKE_COLOR_DARK = new paper.Color('#000');
    private AREA_STROKE_COLOR_LIGHT = new paper.Color('#DDD');
    private AREA_STROKE_WIDTH = 1;
    private AREA_STROKE_WIDTH_SELECTED = 2;

    private plugins = [
        new PlacementAreaDesignerAreaSizePlugin(),
        new placementAreaDesignerDocumentSizePlugin()
        // new PlacementAreaDesignerToolToolipPlugin()
    ];

    private canvas!: HTMLCanvasElement;
    private context!: RenderingContext;
    private areasLayer!: paper.Layer;
    private handlesLayer!: paper.Layer;
    private itemsLayer!: paper.Layer;
    private pdfLayer!: paper.Layer;

    private handles: paper.Group[] = [];
    private handleContainer!: paper.Group;
    private handlesBorderPath!: paper.Path;

    private isRotating = false;
    private isResizing = false;
    private isMoving = false;
    private isMovingView = false;
    private hasViewMoved = false;

    private multipleSelection = false;

    public areas: Area[] = [];
    public items: PlacementItem[] = [];
    public layers: PlacementLayer[] = [];
    public ppcm: number = 100;
    public unit: Unit = units[0];

    private originalAreaBounds = new paper.Rectangle(0, 0, 1, 1);
    private originalPPCM = 0;
    private panDeltaFromCenter = new paper.Point(0, 0);

    private events!: PlacementAreaDesignerEvents;

    private lastViewCenter: paper.Point | null = null;
    private lastMousePoint: paper.Point | null = null;
    private canvasSize: paper.Size = new paper.Size(1, 1);
    private tool: PlacementTool = 'none';

    private snapXPath: paper.Path | null = null;
    private snapYPath: paper.Path | null = null;
    private currentSelectionRectangle: paper.Item | null = null;

    public placementItem: paper.Item | null = null;

    public selectedPaperItem: paper.Item | null | undefined = null;
    public selectedItem: Area | PlacementItem | null | undefined = null;

    public selectedPaperItems: paper.Item[] = [];
    public selectedItems: (Area | PlacementItem)[] = [];

    public selectedItemGuid: string | null | undefined = null;
    public selectedItemsGuid: string[] = [];

    public edgeselectedItems: Map<string, paper.Item> = new Map();

    public paper = new paper.PaperScope();
    public panTool = new paper.Tool();
    public multipleSelectionTool = new paper.Tool();

    public readonly mode: Mode;

    private hitOptions = {
        tolerance: 0,
        segments: false,
        stroke: false,
        fill: true,
        curves: false
    };

    private hitOptionsStroke = {
        tolerance: 5,
        segments: false,
        fill: false,
        stroke: false,
        curves: true
    };

    constructor(mode: Mode) {
        this.mode = mode;
    }

    // Factory method
    public static create(canvas: HTMLCanvasElement, mode: Mode) {
        const manager = new PlacementAreaDesignerManager(mode);
        manager.setup(canvas);

        return manager;
    }

    public destroy = () => {
        this.paper.project.remove();
    };

    // Init / setup
    private setup(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.context = canvas.getContext('2d')!;
        this.context.imageSmoothingQuality = 'high';
        this.context.imageSmoothingEnabled = true;

        this.paper.setup(canvas);

        this.areasLayer = new paper.Layer();
        this.handlesLayer = new paper.Layer();
        this.itemsLayer = new paper.Layer();
        this.pdfLayer = new paper.Layer();

        this.areasLayer.activate();

        if (this.mode === 'PLACEMENT') {
            this.panTool.onMouseDown = this.onPanMouseDown;
            this.panTool.onMouseMove = this.onPanMouseMove;
            this.panTool.onMouseUp = this.onPanMouseUp;
            this.panTool.onKeyDown = this.onPanKeyDown;
            this.panTool.onKeyUp = this.onPanKeyUp;

            this.multipleSelectionTool.onMouseDown = this.onMultipleSelectionMouseDown;
            this.multipleSelectionTool.onMouseUp = this.onMultipleSelectionMouseUp;
            this.multipleSelectionTool.onMouseDrag = this.onMultipleSelectionMouseDrag;
            this.multipleSelectionTool.onMouseMove = this.onMultipleSelectionMouseMove;
            this.multipleSelectionTool.onKeyUp = this.onMultipleSelectionKeyUp;

            this.createHandles();
        }

        this.callPlugin((plugin) => plugin.create(this));

        this.areasLayer.activate(); // Avoid activation of plugins custom layers

        window.addEventListener('resize', this.updateCanvasSize);
        this.updateCanvasSize();
        this.paper.view.center = new paper.Point(0, 0);
    }

    // // Change ppcm
    // public setPPCM = (ppcm: number) => {
    //     if (ppcm != this.ppcm) {
    //         this.ppcm = ppcm;
    //     }
    // }

    // Keep updated events
    public setEvents = (events: PlacementAreaDesignerEvents) => {
        this.events = events;
    };

    public setUnit = (unit: Unit) => {
        if (unit.value != this.unit.value) {
            this.unit = unit;
            this.callPlugin((plugin) => plugin.onUnitChange?.());
        }
    };

    public setPlacementSize(widthCM: number | undefined, heightCM: number | undefined) {
        if (widthCM && heightCM) {
            this.placementItem?.remove();
            const heightPX = heightCM * this.ppcm;
            const widthPX = widthCM * this.ppcm;
            this.placementItem = new paper.Path.Rectangle(
                new paper.Rectangle({ x: -widthPX / 2, y: -heightPX / 2, width: widthPX, height: heightPX }),
                new paper.Size(1, 1)
            );
            this.placementItem.data.width = widthCM;
            this.placementItem.data.height = heightCM;
            this.placementItem.data.unit = this.unit;
            this.placementItem.strokeColor = new paper.Color('#000000');
            this.placementItem.strokeScaling = false;
            this.fitToPlacementDocument();
        }
    }

    public setZIndex(value: ZIndexType | null) {
        if (value === 'FRONT') this.selectedPaperItem?.bringToFront();
        else if (value === 'BACK') this.selectedPaperItem?.sendToBack();
    }

    public setTool = (tool: PlacementTool) => {
        if (this.tool != tool) {
            switch (tool) {
                case 'pan':
                    this.panTool.activate();
                    this.canvas.style.cursor = 'default';
                    break;
                case 'multipleSelection':
                    if (!this.isMoving) {
                        this.multipleSelectionTool.activate();
                        this.canvas.style.cursor = 'crosshair';
                    }
                    break;
            }

            this.callPlugin((plugin) => plugin.onToolChanged?.(this.tool, tool));
            this.tool = tool;
        }
    };

    // Keep updated areas
    public setAreas = (areas: Area[]) => {
        this.areas = areas;
        let areasChanged = false;

        // Remove areas that does not exist
        for (const item of this.getAreasItems()) {
            const area = this.getAreaByGuid(item.data.guid);

            if (!area) {
                item.remove();

                // for now, I keep the area layers (bleed) not visible
                // this.callPlugin(cb => cb.onAreaItemRemoved?.(item, item.data.guid));
            }
        }

        // Add new areas
        for (const area of this.areas) {
            let item = this.getItemByGuid(area.guid);

            if (!item) {
                item = this.createItemForArea(area);
                areasChanged = true;

                // for now, I keep the area layers (bleed) not visible
                // if (item) {
                //     this.callPlugin(cb => cb.onAreaItemCreated?.(item!, area));
                // }
            } else {
                if (this.isAreaChanged(item, area)) {
                    item.remove(); // Remove the old one, types may be different
                    item = this.areasLayer.importJSON(area.path);

                    item.data.guid = area.guid;
                    item.data.isArea = true;
                    item.data.visible = item.visible;
                    item.data.labelVisibility = area.labelVisibility ?? true;
                    item.data.rotation = area.rotation;
                    areasChanged = true;
                    item.visible = area.visible ?? true;

                    // for now, I keep the area layers (bleed) not visible
                    // if (item)
                    //     this.callPlugin(cb => cb.onAreaItemUpdated?.(item!, area));
                } else if (this.isVisibilityChanged(item, area)) {
                    item.visible = area.visible ?? true;
                    item.data.labelVisibility = area.labelVisibility ?? true;
                }
            }

            if (!area.originalBoundsHeight && !area.originalBoundsWidth) {
                area.originalBoundsHeight = item.bounds.height;
                area.originalBoundsWidth = item.bounds.width;
            }
        }

        if (areasChanged) {
            this.updateAreasColors();
        }
    };

    // Keep updated areas
    public setLayers = (layers: PlacementLayer[]) => {
        this.layers = layers;

        // Remove areas that does not exist
        for (const placementLayers of this.getPlacementLayers()) {
            const layer = this.getLayerByGuid(placementLayers.data.guid);

            if (!layer) {
                placementLayers.remove();
                // this.callPlugin(cb => cb.onAreaItemRemoved?.(placementLayers, placementLayers.data.guid));
            }
        }

        // Add new layer
        for (const layer of this.layers) {
            let item = this.getPlacementLayers().find((l) => l.data.guid === layer.guid);

            if (!item) {
                item = this.createItemForPlacementLayer(layer);
                // areasChanged = true;

                // if (item) {
                //     this.callPlugin(cb => cb.onAreaItemCreated?.(item!, area));
                // }
            }
        }
    };

    // Keep updated items
    public setItems = (items: PlacementItem[]) => {
        this.items = items;
        let itemsChanged = false;

        //Remove items that does not exist
        for (const item of this.getPlacementItems()) {
            const found = this.items.find((i) => i.guid == item.data.guid);

            if (!found) {
                item.remove();

                // for now, I keep the area layers (bleed) not visible
                // this.callPlugin(cb => cb.onAreaItemRemoved?.(item, item.data.guid));
            }
        }

        for (const item of this.items) {
            let paperItem = this.getItemByGuid(item.guid);

            if (!paperItem) {
                //it's a new item
                paperItem = this.createItemForPlacementItem(item);
                itemsChanged = true;

                // if (paperItem) {
                //     this.callPlugin(cb => cb.onAreaItemCreated?.(paperItem!, item));
                // }
            } else {
                //the item already exist
                if (this.isAreaChanged(paperItem, item) || this.isTextChanged(paperItem, item)) {
                    paperItem.position.x = item.boundsX + item.boundsWidth / 2;
                    paperItem.position.y = item.boundsY + item.boundsHeight / 2;
                    paperItem.data.rotation = item.rotation;
                    paperItem.rotation = item.rotation;
                    paperItem.scaling = new this.paper.Point(item.scaling, item.scaling);

                    paperItem.data.guid = item.guid; // Force guid update

                    if (paperItem instanceof paper.PointText) {
                        if (item.content) paperItem.content = item.content;

                        if (item.color) paperItem.fillColor = new paper.Color(item.color);

                        //da problemi con lo scaling
                        // if (item.fontSize)
                        // paperItem.fontSize = item.fontSize;

                        if (item.fontFamily) paperItem.fontFamily = item.fontFamily;

                        if (item.textAlignment) paperItem.justification = item.textAlignment;
                    }

                    itemsChanged = true;
                    // if (paperItem)
                    //     this.callPlugin(cb => cb.onAreaItemUpdated?.(paperItem!, item));
                }
            }
        }

        if (itemsChanged) {
            this.updateAreasColors();
        }
    };

    private isAreaChanged = (item: paper.Item, area: Area | PlacementItem) => {
        return (
            Math.abs(item.bounds.x - area.boundsX) > 0.01 ||
            Math.abs(item.bounds.y - area.boundsY) > 0.01 ||
            Math.abs(item.bounds.width - area.boundsWidth) > 0.01 ||
            Math.abs(item.bounds.height - area.boundsHeight) > 0.01
        );
    };

    private isVisibilityChanged = (item: paper.Item, area: Area | PlacementItem) => {
        return item.visible !== area.visible || item.data.labelVisibility !== area.labelVisibility;
    };

    private isTextChanged = (paperItem: paper.Item, placementItem: PlacementItem) => {
        return (
            paperItem instanceof paper.PointText &&
            paperItem.data.isText &&
            (paperItem.fontFamily !== placementItem.fontFamily ||
                paperItem.fontSize !== placementItem.fontSize ||
                paperItem.fillColor?.toCSS(true) !== placementItem.color ||
                paperItem.content !== placementItem.content)
        );
    };

    public setSelectedItemGuid = (guid: string | null | undefined) => {
        this.selectedItemGuid = guid;
    };

    // Keep selected area updated
    public setSelectedAreas = (guids: string[]) => {
        this.selectedItemsGuid = guids;

        const previousSelectedItem = this.selectedItem;
        const previousSelectedPaperItem = this.selectedPaperItem;

        this.selectedItems = [...this.areas, ...this.items].filter((item) => guids.includes(item.guid)) ?? [];
        this.selectedPaperItems = this.getItems().filter((item) => guids.includes(item.data.guid)) ?? [];
        this.selectedPaperItem = this.selectedPaperItems.find((x) => x.data.guid === this.selectedItemGuid);

        this.updateAreasColors();

        this.callPlugin((plugin) => {
            if (this.selectedItem && this.selectedPaperItem) plugin.onAreaSelectionChanged?.(this.selectedPaperItem, this.selectedItem, true);

            if (previousSelectedItem && previousSelectedPaperItem) plugin.onAreaSelectionChanged?.(previousSelectedPaperItem, previousSelectedItem, false);
        });
    };

    public zoomIn = () => {
        this.paper.view.zoom *= 1.1;
        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());
        this.update();
    };

    public zoomOut = () => {
        this.paper.view.zoom /= 1.1;
        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());
        this.update();
    };

    public fitToBounds = () => {
        let bounds: paper.Rectangle = new paper.Rectangle({ x: 0, y: 0, width: 1, height: 1 });
        for (const item of this.getItems()) {
            bounds = bounds.unite(item.bounds);
        }
        const zoom = Math.min(this.canvasSize.width / (bounds.width + bounds.width * 0.1), this.canvasSize.height / (bounds.height + bounds.height * 0.1));
        this.paper.view.zoom = zoom;
        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());
    };

    public fitToPlacementDocument = () => {
        if (!this.placementItem) return;

        let bounds: paper.Rectangle = new paper.Rectangle({ x: 0, y: 0, width: 1, height: 1 });
        bounds = bounds.unite(this.placementItem.bounds);

        const zoom = Math.min(this.canvasSize.width / (bounds.width + bounds.width * 0.2), this.canvasSize.height / (bounds.height + bounds.height * 0.2));
        this.paper.view.zoom = zoom;
        this.callPlugin((plugin) => plugin.onViewZoomChanged?.());

        this.callPlugin((plugin) => plugin.onDocumentSizeChanged?.());
    };

    public update = () => {
        this.hitOptionsStroke.tolerance = Math.max(10, Math.round(10 / this.paper.view.zoom));
        this.resetHandles();
    };

    private callPlugin = (cb: (plugin: PlacementAreaDesignerPlugin) => void) => {
        this.plugins.forEach(cb);
    };

    private createHandles = () => {
        const iconSize = 24;
        const rect = new paper.Rectangle({ x: 0, y: 0, width: iconSize, height: iconSize });
        const radius = new paper.Size(3, 3);

        const moveHandle = new paper.Group();
        const deleteHandle = new paper.Group();
        const resizeHandle = new paper.Group();
        const rotateHandle = new paper.Group();

        const moveRectangle = new paper.Path.Rectangle(rect, radius);
        const deleteRectangle = new paper.Path.Rectangle(rect, radius);
        const resizeRectangle = new paper.Path.Rectangle(rect, radius);
        const rotateRectangle = new paper.Path.Rectangle(rect, radius);

        const moveIcon = this.paper.project.importSVG(MoveIcon);
        const deleteIcon = this.paper.project.importSVG(DeleteIcon);
        const rotateIcon = this.paper.project.importSVG(RotateIcon);
        const resizeIcon = this.paper.project.importSVG(ResizeIcon);
        resizeIcon.rotate(45);

        moveHandle.addChildren([moveRectangle, moveIcon]);
        deleteHandle.addChildren([deleteRectangle, deleteIcon]);
        resizeHandle.addChildren([resizeRectangle, resizeIcon]);
        rotateHandle.addChildren([rotateRectangle, rotateIcon]);

        const handles = [moveHandle, deleteHandle, rotateHandle, resizeHandle];

        [moveRectangle, deleteRectangle, rotateRectangle, resizeRectangle].forEach((rectangle) => {
            rectangle.fillColor = this.HANDLE_COLOR;
            rectangle.strokeWidth = 1;
            rectangle.strokeScaling = false;
            rectangle.strokeColor = new paper.Color(theme.app.primary_dark);
            rectangle.data.isHandleRectangle = true;
        });

        [moveIcon, deleteIcon, rotateIcon, resizeIcon].forEach((icon) => {
            icon.position = new paper.Point(iconSize / 2, iconSize / 2);
            icon.scale(0.7);

            getItemChildrens(icon).forEach((child) => {
                child.data.isHandleIcon = true;
            });
        });

        handles.forEach((handle) => {
            handle.applyMatrix = false;
            handle.data.isHandle = true;
        });

        this.handleContainer = new this.paper.Group();
        this.handleContainer.addChildren(handles);

        this.handlesLayer.addChild(this.handleContainer);

        this.handles = handles;
    };

    private applyAreaStyle = (item: paper.Item) => {
        item.strokeColor = this.AREA_STROKE_COLOR_DARK;
        item.dashArray = [5, 5];
        item.strokeWidth = this.AREA_STROKE_WIDTH;
        item.strokeScaling = false;
        item.applyMatrix = true;
        item.fillColor = new paper.Color('#E0E0E0');
        item.fillColor.alpha = 0.3;
    };

    private applySelectionRectangleStyle = (item: paper.Item) => {
        item.strokeColor = this.AREA_STROKE_COLOR_DARK;
        item.dashArray = [5, 5];
        item.strokeWidth = this.AREA_STROKE_WIDTH;
        item.strokeScaling = false;
        item.applyMatrix = true;
    };

    private updateAreasColors = () => {
        // Update area stroke width and color
        for (const item of this.getAreasItems()) {
            this.applyAreaStyle(item);
        }

        this.edgeselectedItems.forEach((item) => item.remove());
        this.edgeselectedItems.clear();

        if (this.selectedPaperItems.length == 1) {
            this.selectedPaperItems.forEach((paperItem) => {
                paperItem.strokeWidth = this.AREA_STROKE_WIDTH_SELECTED;

                if (paperItem.data.isArea) {
                    paperItem.fillColor = new paper.Color('#C0C0E0');
                    paperItem.fillColor.alpha = 0.5;
                }
            });
        } else if (this.selectedPaperItems.length > 1) {
            this.selectedPaperItems.forEach((paperItem) => {
                paperItem.strokeWidth = this.AREA_STROKE_WIDTH_SELECTED;

                if (paperItem.data.isArea) {
                    paperItem.fillColor = new paper.Color('#C0C0E0');
                    paperItem.fillColor.alpha = 0.5;
                } else {
                    const paperEdge = new paper.Path.Rectangle(paperItem.bounds);
                    paperEdge.strokeWidth = this.AREA_STROKE_WIDTH_SELECTED;
                    paperEdge.strokeScaling = false;
                    paperEdge.strokeColor = new paper.Color(0.5, 0.5, 0.5);
                    paperEdge.dashArray = [10, 10];
                    paperEdge.applyMatrix = true;
                    this.edgeselectedItems.set(paperItem.data.guid, paperEdge);
                }
            });
        }
    };

    private resetHandles = () => {
        // if (!this.selectedItem)
        this.selectedItem = this.selectedItems?.find((x) => this.selectedItemGuid === x.guid);

        this.selectedPaperItem = this.selectedPaperItems.find((x) => x.data.guid === this.selectedItemGuid);

        if (this.selectedPaperItem && this.selectedItem) {
            this.handleContainer.rotation = 0;
            this.handleContainer.position = this.selectedPaperItem.position;

            const offset = this.handles[0].bounds.width / 2;

            this.handles[0].position = this.selectedPaperItem.bounds.topLeft.add(new paper.Point(-offset, -offset));
            this.handles[1].position = this.selectedPaperItem.bounds.bottomLeft.add(new paper.Point(-offset, offset));
            this.handles[2].position = this.selectedPaperItem.bounds.topRight.add(new paper.Point(offset, -offset));
            this.handles[3].position = this.selectedPaperItem.bounds.bottomRight.add(new paper.Point(offset, offset));

            this.handles.forEach((handle) => {
                handle.scaling = new this.paper.Point(1 / this.paper.view.zoom, 1 / this.paper.view.zoom);
                handle.visible = true;
                handle.bringToFront();
            });

            // for now, I keep the area layers (bleed) not visible
            // Hide rotation for areas with layers
            // this.handles[2].visible = true;
            // if ('layers' in this.selectedItem) {
            //     this.handles[2].visible = this.selectedItem!.layers.length === 0;
            // }

            // Hide resize for areas
            this.handles[3].visible = true;
            if (this.selectedItem && isArea(this.selectedItem)) {
                this.handles[3].visible = false;
            }

            // if (this.selectedPaperItems.length > 1) {
            //     this.handles[1].visible = false;
            //     this.handles[2].visible = false;
            //     this.handles[3].visible = false;
            // }

            if (this.handlesBorderPath) {
                this.handlesBorderPath.remove();
            }

            this.handlesBorderPath = new paper.Path.Rectangle(this.selectedPaperItem.bounds);

            this.handleContainer.addChild(this.handlesBorderPath);
            this.handlesBorderPath.strokeWidth = 1;
            this.handlesBorderPath.strokeScaling = false;
            this.handlesBorderPath.strokeColor = new paper.Color(0.5, 0.5, 0.5);
            this.handlesBorderPath.dashArray = [10, 10];
            this.handlesBorderPath.applyMatrix = false;
            this.handlesBorderPath.sendToBack();
            this.handlesLayer.bringToFront();
        } else {
            this.handlesBorderPath?.remove();
            this.handles.forEach((handle) => (handle.visible = false));
        }
    };

    // Better to use the .rotate method because we can't get the current area rotation
    // because we are using applyMatrix = true so the rotation is stored in the path and
    // cannot be retrevied
    private updateHandles = (deltaAngle: number, position: paper.Point, moveHandlesToBounds: boolean) => {
        this.selectedItem = this.selectedItems?.find((x) => x.guid === this.selectedPaperItems.at(-1)?.data.guid);
        this.selectedPaperItem = this.selectedPaperItems.find((x) => x.data.guid === this.selectedItemGuid);

        if (this.selectedPaperItem && this.selectedItem) {
            this.handleContainer.rotate(deltaAngle);
            this.handleContainer.position = position;

            for (const handle of this.handles) handle.rotation = 0;

            if (moveHandlesToBounds) {
                const scaleX = this.selectedPaperItem.bounds.width / this.handlesBorderPath.bounds.width;
                const scaleY = this.selectedPaperItem.bounds.height / this.handlesBorderPath.bounds.height;

                this.handlesBorderPath.scale(scaleX, scaleY);
                this.handlesBorderPath.position = position;

                const offset = this.handles[0].bounds.width / 2;

                this.handles[0].position = this.selectedPaperItem.bounds.topLeft.add(new paper.Point(-offset, -offset));
                this.handles[1].position = this.selectedPaperItem.bounds.bottomLeft.add(new paper.Point(-offset, offset));
                this.handles[2].position = this.selectedPaperItem.bounds.topRight.add(new paper.Point(offset, -offset));
                this.handles[3].position = this.selectedPaperItem.bounds.bottomRight.add(new paper.Point(offset, offset));
            }
        }
    };

    private isHandle = (item: paper.Item) => {
        return item.data.isHandle || item.data.isHandleIcon || item.data.isHandleRectangle;
    };

    private getHandle = (item: paper.Item) => {
        if (this.isHandle(item)) {
            return item.data.isHandleIcon || item.data.isHandleRectangle ? getItemParents(item).find((x) => x.data.isHandle) : item;
        } else {
            return null;
        }
    };

    public updateCanvasSize = () => {
        if (this.canvas && this.paper) {
            this.canvasSize = new paper.Size(
                this.canvas.parentNode ? (this.canvas.parentNode as HTMLElement).offsetWidth : this.canvas.width,
                this.canvas.parentNode ? (this.canvas.parentNode as HTMLElement).offsetHeight : this.canvas.height
            );
            this.paper.view.viewSize = this.canvasSize;

            this.callPlugin((plugin) => {
                plugin.onViewMoved?.();
                plugin.onViewZoomChanged?.();
            });
        }
    };

    public getItems = () => {
        return [...this.areasLayer.children, ...this.itemsLayer.children] as paper.Item[];
    };

    public getAreasItems = () => {
        return this.getItems().filter((item) => item.data && item.data.isArea);
    };

    public getPlacementItems = () => {
        return this.itemsLayer.children as paper.Item[];
    };

    public getPlacementLayers = () => {
        return this.pdfLayer.children as paper.Item[];
    };

    public updateAreas = (items: paper.Item[], newPPCM: number | null) => {
        const clonedAreas = items.map((item) => {
            const area = [...this.areas, ...this.items].find((area) => area.guid == item.data.guid);

            if (!area) throw new Error(`Can't update area ${item.data.guid}: area not found.`);

            const clonedItem = item.clone();
            clonedItem.strokeWidth = 1;
            clonedItem.strokeColor = item.strokeColor;
            clonedItem.strokeScaling = true;

            const clonedArea = _.clone(area);
            clonedArea.path = clonedItem.exportJSON();
            clonedArea.pathSVG = clonedItem.exportSVG({ asString: true }) as string;
            clonedArea.boundsX = item.bounds.x;
            clonedArea.boundsY = item.bounds.y;
            clonedArea.boundsWidth = item.bounds.width;
            clonedArea.boundsHeight = item.bounds.height;
            clonedArea.rotation = item.data.rotation ?? 0;

            if (isPlacementItem(clonedArea)) clonedArea.scaling = item.scaling.x; //TODO check it

            clonedItem.remove();

            return clonedArea;
        });

        if (clonedAreas) this.events.onItemsChanged?.(clonedAreas, newPPCM, []);
    };

    public getItemByGuid = (guid: string) => {
        return this.getItems().find((item) => item.data.guid == guid);
    };

    public getAreaByGuid = (guid: string) => {
        return this.areas.find((area) => area.guid == guid);
    };

    public getLayerByGuid = (guid: string) => {
        return this.layers.find((layer) => layer.guid == guid);
    };

    private createItemForArea = (area: Area) => {
        const item = this.areasLayer.importJSON(area.path);
        item.visible = area.visible ?? true;
        item.data.guid = area.guid; // Force guid update
        item.data.isArea = true;
        item.data.itemName = area.completeName ?? '';
        item.data.visible = item.visible;
        item.data.labelVisibility = area.labelVisibility ?? true;

        this.applyAreaStyle(item);

        return item;
    };

    private createItemForPlacementItem = (placementItem: PlacementItem) => {
        let item = this.itemsLayer.importJSON(placementItem.path);
        item.data.guid = placementItem.guid; // Force guid update

        return item;
    };

    private createItemForPlacementLayer = (placementLayer: PlacementLayer) => {
        if (placementLayer.jsonArea) {
            const item = this.pdfLayer.importJSON(placementLayer.jsonArea);

            item.position.x = 0;
            item.position.y = 0;
            item.data.guid = placementLayer.guid; // Force guid update
            item.data.isLayer = true;
            item.strokeWidth = 1;
            // item.strokeWidth = 5.0 / this.paper.view.zoom;
            item.strokeScaling = false;

            return item;
        }
    };

    private getRootItem = (item: paper.Item) => {
        let parent = item;

        while (!parent.data || !parent.data.guid) {
            if (parent.parent) parent = parent.parent;
            else break;
        }

        return parent;
    };

    private hitTest = (point: paper.Point) => {
        const hitPoint = this.handlesLayer.hitTest(point, this.hitOptions);
        const handleItem = hitPoint && hitPoint.item && this.isHandle(hitPoint.item) ? this.getHandle(hitPoint.item) : null;

        // const hitPointStroke = this.areasLayer.hitTest(point, this.hitOptionsStroke);
        const hitPointStroke = this.areasLayer.hitTest(point, this.hitOptions);
        const areaRootItem = hitPointStroke?.item ? this.getRootItem(hitPointStroke.item) : null;
        const areaItem = areaRootItem?.data?.isArea ? areaRootItem : null;

        const hitPointFill = this.itemsLayer.hitTest(point, this.hitOptions);
        const itemRootItem = hitPointFill?.item ? this.getRootItem(hitPointFill.item) : null;

        return [handleItem, areaItem || itemRootItem];
    };

    // Snap
    private getSnapPosition = (point: paper.Point) => {
        this.selectedItem = this.selectedItems?.find((x) => this.selectedItemGuid === x.guid);

        const snapAreasItems = this.getAreasItems().filter((x) => x.data.guid !== this.selectedItem?.guid);
        const newPoint = new paper.Point(point);
        let snappedX = false;
        let snappedY = false;

        if (this.selectedPaperItem) {
            const snapPointsX = [0, ...snapAreasItems.map((x) => x.bounds.center.x)];

            const snapPointsY = [0, ...snapAreasItems.map((x) => x.bounds.center.y)];

            for (const snapPointX of snapPointsX) {
                if (Math.abs(snapPointX - point.x) < 5 / this.paper.view.zoom) {
                    newPoint.x = snapPointX;
                    snappedX = true;
                    break;
                }
            }

            for (const snapPointY of snapPointsY) {
                if (Math.abs(snapPointY - point.y) < 5 / this.paper.view.zoom) {
                    newPoint.y = snapPointY;
                    snappedY = true;
                    break;
                }
            }
        }

        return { newPoint, snappedX, snappedY };
    };

    private createSnapGuide = (x1: number, y1: number, x2: number, y2: number) => {
        const path = new this.paper.Path.Line(new this.paper.Point(x1, y1), new this.paper.Point(x2, y2));

        path.strokeWidth = 1;
        path.strokeColor = new this.paper.Color('blue');
        path.strokeWidth = 1.0;
        path.dashOffset = 5;
        path.dashArray = [5, 5];
        path.strokeScaling = false;
        path.removeOn({
            drag: true,
            up: true
        });

        return path;
    };

    // #region Pan tool
    private onPanMouseDown = (e: paper.ToolEvent) => {
        this.lastMousePoint = e.point;

        this.selectedItem = this.selectedItems?.find((x) => this.selectedItemGuid === x.guid);

        const [handle, paperItem] = this.hitTest(e.point);

        if (this.selectedPaperItem && handle) {
            this.originalAreaBounds = this.selectedPaperItem.bounds;
            this.originalPPCM = this.ppcm;

            // First check for handles
            if (handle == this.handles[0]) {
                // move
                this.isMoving = true;

                this.selectedPaperItem = this.selectedPaperItems.find((x) => x.data.guid === this.selectedItem?.guid);

                if (this.selectedPaperItem) {
                    this.panDeltaFromCenter = this.selectedPaperItem.bounds.center.subtract(e.point);
                    this.callPlugin((plugin) => plugin.onAreaMoveStart?.(this.selectedPaperItem!, this.selectedItem!, e));
                }
            } else if (handle == this.handles[1]) {
                // remove
                this.events.onAreaRemoved?.(this.selectedItem!);
                this.callPlugin((plugin) => plugin.onAreaRemoved?.(this.selectedPaperItem!, this.selectedItem!, e));
            } else if (handle == this.handles[2]) {
                // rotate
                this.isRotating = true;
                this.callPlugin((plugin) => plugin.onAreaRotateStart?.(this.selectedPaperItem!, this.selectedItem!, e));
            } else if (handle == this.handles[3]) {
                // resize
                this.isResizing = true;
                this.callPlugin((plugin) => plugin.onAreaResizeStart?.(this.selectedPaperItem!, this.selectedItem!, e));
            }

            this.resetHandles();
        } else if (paperItem) {
            // Then for area
            this.panDeltaFromCenter = paperItem.bounds.center.subtract(e.point);
            this.isMoving = true;
            this.originalAreaBounds = paperItem.bounds;
            this.originalPPCM = this.ppcm;

            // this.events.onItemSelected?.([...this.areas, ...this.items].find(item => item.guid == paperItem.data.guid), this.multipleSelection);
            if (this.selectedItemsGuid.length > 0 && this.selectedItemsGuid.includes(paperItem.data.guid))
                this.events.onItemsSelected?.(
                    [...this.areas, ...this.items].filter((item) => this.selectedItemsGuid.includes(item.guid)),
                    paperItem.data.guid
                );
            else
                this.events.onItemsSelected?.(
                    [...this.areas, ...this.items].filter((item) => item.guid == paperItem.data.guid),
                    paperItem.data.guid
                );

            this.callPlugin((plugin) => plugin.onAreaMoveStart?.(this.selectedPaperItem!, this.selectedItem!, e));

            this.resetHandles();
        } else {
            // Then move view
            this.isMovingView = true;
        }

        this.callPlugin((plugin) => plugin.onMouseDown?.(e));
    };

    private onPanMouseUp = (e: paper.ToolEvent) => {
        const [handle, areaItem] = this.hitTest(e.point);

        this.selectedItem = this.selectedItems?.find((x) => this.selectedItemGuid === x.guid);

        if (this.selectedItem && this.selectedPaperItem && (this.isResizing || this.isMoving || this.isRotating)) {
            // If bleed lines, change also the PPCM, you can't change their physical size.
            let newPPCM: number | null = null;

            // for now, I keep the area layers (bleed) not visible
            // if ('layers' in this.selectedItem && this.selectedItem.layers.length > 0) {
            //     // Get the old physical size, and keep it.
            //     const scale = this.originalAreaBounds.width / this.selectedPaperItem.bounds.width;
            //     newPPCM = this.originalPPCM / scale;
            // }

            this.updateAreasColors();
            this.updateAreas(this.selectedPaperItems, newPPCM);
        } else if (!handle && !areaItem && !this.hasViewMoved) {
            // this.events.onItemSelected?.(null);
            this.events.onItemsSelected?.(null);
        }

        this.callPlugin((plugin) => {
            if (this.isMoving) plugin.onAreaMoveEnd?.(this.selectedPaperItem!, this.selectedItem!, e);

            if (this.isResizing) plugin.onAreaResizeEnd?.(this.selectedPaperItem!, this.selectedItem!, e);

            if (this.isRotating) plugin.onAreaRotateEnd?.(this.selectedPaperItem!, this.selectedItem!, e);

            plugin.onMouseUp?.(e);
        });

        this.isRotating = false;
        this.isResizing = false;
        this.isMoving = false;
        this.isMovingView = false;
        this.hasViewMoved = false;
    };

    private onPanMouseMove = (e: paper.ToolEvent) => {
        this.selectedItem = this.selectedItems?.find((x) => this.selectedItemGuid === x.guid);

        for (const handle of this.handles) {
            handle.fillColor = this.HANDLE_COLOR;
            handle.children[1].fillColor = this.HANDLE_ICON_COLOR;
        }

        const [handle, areaItem, item] = this.hitTest(e.point);

        if (this.canvas) {
            if (handle) {
                handle!.children[0].fillColor = this.HANDLE_HOVER_COLOR;
                handle!.children[1].fillColor = this.HANDLE_COLOR;

                let cursor = 'pointer';

                if (handle == this.handles[0]) cursor = 'move';

                if (handle == this.handles[3]) cursor = 'nw-resize';

                this.canvas.style.cursor = cursor;
            } else if (areaItem) {
                this.canvas.style.cursor = 'move';
            } else if (item) {
                this.canvas.style.cursor = 'move';
            } else {
                this.canvas.style.cursor = 'default';
            }
        }

        if (this.selectedPaperItem && this.selectedItem) {
            if (this.isRotating) {
                const dir = e.point.subtract(this.selectedPaperItem.position);
                const prevDir = e.lastPoint.subtract(this.selectedPaperItem.position);
                const angle = prevDir.getDirectedAngle(dir);

                this.selectedPaperItem.rotate(angle);

                // if (!this.selectedItem.rotation)
                //     this.selectedItem.rotation = 0;

                // this.selectedItem.rotation += angle;

                this.selectedPaperItem.data.rotation = (this.selectedPaperItem.data.rotation ?? 0) + angle;

                this.callPlugin((plugin) => plugin.onAreaRotating?.(this.selectedPaperItem!, this.selectedItem!, e));

                this.handleContainer.rotate(angle, this.selectedPaperItem.position);

                for (const handle of this.handles) handle.rotation = 0;
            } else if (this.isResizing) {
                const oldDiagonal = this.selectedPaperItem.bounds.bottomRight.subtract(this.selectedPaperItem.bounds.topLeft);
                const currentDistancePoint = e.point.subtract(this.selectedPaperItem.bounds.topLeft);

                if (currentDistancePoint.x >= 0 && currentDistancePoint.y >= 0) {
                    // Bleed lines must have proportional scaling
                    // const isBleedMode = this.selectedArea.layers.length > 0;
                    const scaleX = Math.max(0.1, currentDistancePoint.x / oldDiagonal.x);
                    const scaleY = scaleX;

                    this.selectedPaperItem.scale(scaleX, scaleY, this.selectedPaperItem.bounds.topLeft);
                    var textScaleX = this.selectedPaperItem.scaling.x;
                    var textScaleY = this.selectedPaperItem.scaling.y;

                    // after the scale, calculate the new fontSize
                    if (this.selectedPaperItem instanceof paper.PointText && 'fontSize' in this.selectedItem) {
                        this.selectedItem.fontSize = (this.selectedPaperItem.fontSize as number) * Math.max(textScaleX, textScaleY);
                        // this.selectedItem.scaling = this.selectedPaperItem.scaling.x;
                    }

                    // // Update PPCM in real time when resizing to keep the layers resize accordly
                    // if (isBleedMode) {
                    //     // Get the old physical size, and keep it.
                    //     const scale = this.originalAreaBounds.width / this.selectedItem.bounds.width;
                    //     this.ppcm = this.originalPPCM / scale;
                    // }

                    // for now, I keep the area layers (bleed) not visible
                    // this.callPlugin(plugin => plugin.onAreaResizing?.(this.selectedPaperItem!, this.selectedItem!, e));

                    this.updateHandles(0, this.selectedPaperItem.bounds.center, true);
                }
            } else if (this.isMoving) {
                this.selectedPaperItem = this.selectedPaperItems.find((x) => x.data.guid === this.selectedItem?.guid);

                const targetPosition = e.point.add(this.panDeltaFromCenter);

                const { newPoint, snappedX, snappedY } = this.getSnapPosition(targetPosition);

                if (
                    this.selectedPaperItems &&
                    this.selectedPaperItems.length > 0 &&
                    this.selectedItems &&
                    this.selectedItems.length > 0 &&
                    this.selectedItems !== undefined
                ) {
                    if (this.selectedPaperItem) {
                        const previousPosition = this.selectedPaperItem.position;
                        this.selectedPaperItem.position = newPoint;
                        const delta = newPoint.subtract(previousPosition);

                        this.selectedPaperItems
                            .filter((x) => x.data.guid !== this.selectedItem?.guid)
                            .forEach((item) => {
                                item.position = item.position.add(delta);
                            });
                    }
                }

                // Snap lines
                if (this.snapXPath) this.snapXPath.remove();

                if (this.snapYPath) this.snapYPath.remove();

                if (snappedX) {
                    this.snapXPath = this.createSnapGuide(newPoint.x, this.paper.view.bounds.top, newPoint.x, this.paper.view.bounds.bottom);
                }

                if (snappedY) {
                    this.snapYPath = this.createSnapGuide(this.paper.view.bounds.left, newPoint.y, this.paper.view.bounds.right, newPoint.y);
                }

                // Update plugins
                this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedPaperItem!, this.selectedItem!, e));

                if (this.selectedPaperItem) this.handleContainer.position = this.selectedPaperItem.position;

                //update the position of all edge
                this.selectedPaperItems.forEach((paperItem) => {
                    const guid = paperItem.data.guid;
                    if (this.edgeselectedItems.has(guid)) {
                        let oldPaperEdge = this.edgeselectedItems.get(guid);
                        let paperEdge = oldPaperEdge?.clone();
                        oldPaperEdge?.remove();

                        if (paperEdge) {
                            paperEdge.position = paperItem.position;
                            this.edgeselectedItems.set(guid, paperEdge);
                        }
                    }
                });
            }
        }

        if (this.isMovingView) {
            this.hasViewMoved = true;
            this.lastViewCenter = this.paper.view.center;

            if (this.lastMousePoint) {
                this.paper.view.center = this.paper.view.center.add(this.lastMousePoint.subtract(e.point));
            }

            this.lastMousePoint = e.point.add(this.paper.view.center.subtract(this.lastViewCenter));
            this.callPlugin((plugin) => plugin.onViewMoved?.());
        }

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    private onPanKeyDown = (e: paper.KeyEvent) => {
        let itemChanged = false;

        this.selectedItem = this.selectedItems?.find((x) => this.selectedItemGuid === x.guid);

        if (this.selectedPaperItem && this.selectedItem) {
            switch (e.key) {
                case 'left':
                    this.selectedPaperItem.position.x -= 1 / this.paper.view.zoom;
                    itemChanged = true;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedPaperItem!, this.selectedItem!, e));
                    break;
                case 'right':
                    this.selectedPaperItem.position.x += 1 / this.paper.view.zoom;
                    itemChanged = true;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedPaperItem!, this.selectedItem!, e));
                    break;
                case 'up':
                    this.selectedPaperItem.position.y -= 1 / this.paper.view.zoom;
                    itemChanged = true;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedPaperItem!, this.selectedItem!, e));
                    break;
                case 'down':
                    this.selectedPaperItem.position.y += 1 / this.paper.view.zoom;
                    itemChanged = true;
                    this.callPlugin((plugin) => plugin.onAreaMoving?.(this.selectedPaperItem!, this.selectedItem!, e));
                    break;
                case 'delete':
                    this.events.onAreaRemoved?.(this.selectedItem);
                    break;
                case 'control':
                    this.setTool('multipleSelection');
                    break;
            }

            if (itemChanged) this.updateAreas(this.selectedPaperItems, null);
        } else {
            switch (e.key) {
                case 'control':
                    this.setTool('multipleSelection');
                    break;
            }
        }

        this.callPlugin((plugin) => plugin.onKeyDown?.(e));
    };

    private onPanKeyUp = (e: paper.KeyEvent) => {
        switch (e.key) {
            default:
                this.setTool('pan');
                this.currentSelectionRectangle = null;
                break;
        }

        this.callPlugin((plugin) => plugin.onKeyUp?.(e));
    };

    private onMultipleSelectionKeyUp = (e: paper.KeyEvent) => {
        switch (e.key) {
            default:
                this.setTool('pan');
                this.currentSelectionRectangle = null;
                break;
        }

        this.callPlugin((plugin) => plugin.onKeyUp?.(e));
    };

    // #region multipleSelection tool

    private onMultipleSelectionMouseDown = (e: paper.ToolEvent) => {
        if (this.currentSelectionRectangle !== null) {
            this.currentSelectionRectangle.remove();
            this.currentSelectionRectangle = null;
        }
    };

    private onMultipleSelectionMouseDrag = (e: paper.ToolEvent) => {
        const startPoint = e.downPoint;
        const currentPoint = e.point;

        if (this.currentSelectionRectangle == null) {
            this.currentSelectionRectangle = new this.paper.Path.Rectangle(startPoint, new this.paper.Size(100, 100));
            this.currentSelectionRectangle.data = {};
            this.currentSelectionRectangle.data.isArea = true;
            this.applySelectionRectangleStyle(this.currentSelectionRectangle);
        }
        const oldDiagonal = this.currentSelectionRectangle.bounds.bottomRight.subtract(this.currentSelectionRectangle.bounds.topLeft);
        const currentDistancePoint = currentPoint.subtract(this.currentSelectionRectangle.bounds.topLeft);

        if (currentDistancePoint.x >= 0 && currentDistancePoint.y >= 0) {
            const scaleX = Math.max(0.1, currentDistancePoint.x / oldDiagonal.x);
            const scaleY = Math.max(0.1, currentDistancePoint.y / oldDiagonal.y);

            this.currentSelectionRectangle.scale(scaleX, scaleY, this.currentSelectionRectangle.bounds.topLeft);

            this.callPlugin((plugin) => plugin.onAreaCreating?.(e));
        }

        this.callPlugin((plugin) => plugin.onMouseMove?.(e));
    };

    private onMultipleSelectionMouseUp = (e: paper.ToolEvent) => {
        // for (const item of this.getItems()) {
        let selectedGuids = [] as string[];

        if (this.currentSelectionRectangle)
            selectedGuids = this.getItems()
                .filter((item) => item.data.guid && this.currentSelectionRectangle?.contains(item.bounds))
                .map((x) => x.data.guid);

        // if (this.currentSelectionRectagle.contains(item.bounds)) {
        this.events.onItemsSelected?.([...this.areas, ...this.items].filter((i) => selectedGuids.includes(i.guid)));
        // this.events.onItemSelected?.([...this.areas, ...this.items].find(i => i.guid == item.data.guid), true);
        // }

        if (this.currentSelectionRectangle) {
            this.currentSelectionRectangle.remove();
            this.currentSelectionRectangle = null;
        }
    };

    private onMultipleSelectionMouseMove = (e: paper.ToolEvent) => {
        this.canvas.style.cursor = 'crosshair';
        // this.callPlugin(plugin => plugin.onMouseMove?.(e));
    };
}
